import clsx from 'clsx';
import { forwardRef, LegacyRef, useEffect, useRef, useState } from 'react';
import { MessageBubble } from './MessageBubble';
import { SupportTicketUpdate } from '../../../types/commonTypes';
import { xsScreen } from '../../../lib/constants';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

interface Props {
  ticketMessages: SupportTicketUpdate[];
}

export const ViewTicketContent = ({ ticketMessages }: Props) => {
  const [scrollTop, setScrollTop] = useState(false);
  const [scrollBottom, setScrollBottom] = useState(false);

  const virtuosoRef = useRef<VirtuosoHandle | null>(null);
  const isMobile = useMediaQuery(xsScreen);
  const classes = useStyles();

  const scrollToMessage = (index: number, alignment: 'start' | 'end') => {
    virtuosoRef.current?.scrollToIndex({
      align: alignment,
      behavior: ticketMessages.length > 25 ? 'auto' : 'smooth',
      index: index,
    });
  };

  useEffect(() => {
    setTimeout(() => scrollToMessage(ticketMessages.length - 1, 'end'), 500);
  }, [ticketMessages]);

  const ItemWrapper = (props: any) => {
    const index = props['data-index'];
    const ticket = ticketMessages[index];
    const isStaff = ticket.type.toLowerCase() === 'staff';
    return (
      <div
        key={`message-${index + 1}`}
        className={clsx('flex w-full', {
          'justify-end': isStaff && !isMobile,
          'justify-start': !isStaff && !isMobile,
          'justify-center': isMobile,
        })}
        {...props}
      />
    );
  };

  const ListWrapper = forwardRef((props: any, ref) => {
    const { style, ...rest } = props;
    return (
      <div
        style={{
          ...style,
          display: 'flex',
          flexDirection: 'column',
          paddingLeft: '25px',
          paddingRight: '25px',
        }}
        ref={ref as LegacyRef<HTMLDivElement>}
        {...rest}
      />
    );
  });

  return (
    <div className="flex flex-col h-[calc(100%-300px)]">
      <div className={classes.quickScroll}>
        {scrollBottom && ticketMessages.length > 1 ? (
          <button
            className={clsx(classes.button, 'rounded-b border-t-0')}
            color="primary"
            onClick={() => scrollToMessage(ticketMessages.length - 1, 'end')}
          >
            Last Message
          </button>
        ) : null}
      </div>

      <Virtuoso
        className={clsx({ '!h-[500px]': !isMobile })}
        atBottomStateChange={(atBottom) =>
          atBottom ? setScrollTop(true) : setScrollTop(false)
        }
        atTopStateChange={(atTop) =>
          atTop ? setScrollBottom(true) : setScrollBottom(false)
        }
        components={{
          Item: ItemWrapper,
          // @ts-ignore
          List: ListWrapper,
        }}
        itemContent={(index) => {
          const message = ticketMessages[index];
          const isStaff = message.type.toLowerCase() === 'staff';

          return (
            <MessageBubble
              message={message}
              messageId={`message-${index}`}
              isStaff={isStaff}
              isLastMessage={ticketMessages.length === index + 1}
              scrollToMessage={() => scrollToMessage(index, 'start')}
            />
          );
        }}
        ref={virtuosoRef}
        style={{ height: '100%', minHeight: 300 }}
        totalCount={ticketMessages.length}
      />

      <div className={classes.quickScroll}>
        {scrollTop && ticketMessages.length > 1 ? (
          <button
            className={clsx(classes.button, 'rounded-t border-b-0')}
            color="primary"
            onClick={() => scrollToMessage(0, 'start')}
          >
            First Message
          </button>
        ) : null}
      </div>
    </div>
  );
};

const useStyles = () => ({
  button:
    'relative h-fit text-[12px] border border-primary text-primary hover:bg-primary/10 uppercase px-3 py-1',
  quickScroll: 'flex justify-end w-full h-4 pr-4',
});
